import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaAPI, clientsAPI, postsAPI, packagesAPI, usersAPI } from '../../services/api';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { handleApiError, getImageUrl, showToast } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES, getDefaultSpecifications } from '../../utils/platformConfig';
import PlatformSelector from '../posts/PlatformSelector';
import ContentTypeSelector from '../posts/ContentTypeSelector';
import SpecificationsForm from '../posts/SpecificationsForm';

const BulkPostCreatorModal = ({ mediaIds, initialClientId, onClose, onSuccess }) => {
  const { user, isAdmin } = useAuth();
  const { isRTL, isArabic } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [writers, setWriters] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState(null); // null = all platforms, array = filtered platforms
  const [clientPackages, setClientPackages] = useState([]); // Store client packages info
  const [totalPostsRemaining, setTotalPostsRemaining] = useState(null); // Total posts remaining across all packages

  const [formData, setFormData] = useState({
    client_id: initialClientId || '',
    platform: 'instagram',
    content_type: 'image_post',
    specifications: {},
    title_prefix: '',
    assigned_designer_id: '',
    assigned_writer_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch available platforms when client is selected
  useEffect(() => {
    if (formData.client_id) {
      fetchAvailablePlatforms();
    } else {
      setAvailablePlatforms(null); // Reset to all platforms
      setClientPackages([]);
      setTotalPostsRemaining(null);
    }
  }, [formData.client_id]);

  // Update specifications when platform/content_type changes
  useEffect(() => {
    if (formData.platform && formData.content_type) {
      const defaultSpecs = getDefaultSpecifications(formData.platform, formData.content_type);
      setFormData(prev => ({
        ...prev,
        specifications: defaultSpecs
      }));
    }
  }, [formData.platform, formData.content_type]);

  const fetchAvailablePlatforms = async () => {
    try {
      const response = await packagesAPI.getAvailablePackages(formData.client_id);
      const packages = response.data.available_packages || [];
      
      console.log('📦 Available packages response:', packages);
      
      // Store packages info
      setClientPackages(packages);
      
      // Calculate total posts remaining
      const totalRemaining = packages.reduce((sum, pkg) => sum + (pkg.posts_remaining || 0), 0);
      setTotalPostsRemaining(totalRemaining);
      
      // Show toast notification if no posts remaining
      if (totalRemaining === 0 && packages.length > 0) {
        showToast.error(
          isRTL 
            ? '⚠️ لا توجد بوستات متبقية في باقات العميل. يرجى إضافة باقة جديدة.' 
            : '⚠️ No posts remaining in client packages. Please add a new package.'
        );
      }
      
      // For carousel: 1 post only. For others: 1 post per file.
      const postsNeeded = formData.content_type === 'carousel' ? 1 : mediaIds.length;
      if (totalRemaining > 0 && totalRemaining < postsNeeded) {
        showToast.error(
          isRTL 
            ? (formData.content_type === 'carousel'
              ? `⚠️ تحتاج بوست واحد للمجموعة. البوستات المتبقية: ${totalRemaining}.`
              : `⚠️ عدد البوستات المتبقية (${totalRemaining}) أقل من عدد الملفات (${mediaIds.length}).`)
            : (formData.content_type === 'carousel'
              ? `⚠️ Need 1 post for carousel. Posts remaining: ${totalRemaining}.`
              : `⚠️ Posts remaining (${totalRemaining}) is less than files (${mediaIds.length}).`)
        );
      }
      
      if (packages.length === 0) {
        // No packages available - allow all platforms (admin might be creating without package check)
        console.log('⚠️ No packages found, allowing all platforms');
        setAvailablePlatforms(null);
        return;
      }

      // Extract unique platforms from all packages
      const platformsSet = new Set();
      let hasAllPlatformsPackage = false;
      
      packages.forEach((pkg, index) => {
        console.log(`📦 Package ${index + 1}:`, {
          name: pkg.package_name,
          platform: pkg.platform,
          platforms: pkg.platforms,
          posts_remaining: pkg.posts_remaining
        });
        
        // Check if package has platforms array (new way) - this is the primary source
        if (pkg.platforms && Array.isArray(pkg.platforms) && pkg.platforms.length > 0) {
          // Add all platforms from this package
          console.log(`  ✅ Using platforms array:`, pkg.platforms);
          pkg.platforms.forEach(plat => platformsSet.add(plat));
        } 
        // Backward compatibility: check platform column (old way) only if platforms array is empty
        else if (pkg.platform && pkg.platform !== 'all' && pkg.platform !== null) {
          console.log(`  ⚠️ Using platform column (backward compat):`, pkg.platform);
          platformsSet.add(pkg.platform);
        }
        // If platform is null or 'all' AND platforms array is empty/null, it means all platforms
        else if ((!pkg.platform || pkg.platform === 'all') && (!pkg.platforms || pkg.platforms.length === 0)) {
          console.log(`  ⚠️ Package allows all platforms (platform: ${pkg.platform}, platforms: ${pkg.platforms})`);
          hasAllPlatformsPackage = true;
        } else {
          console.log(`  ❌ Package has no platforms defined`);
        }
      });

      console.log('📊 Extracted platforms:', Array.from(platformsSet));
      console.log('🌐 Has all platforms package:', hasAllPlatformsPackage);
      console.log('📈 Total posts remaining:', totalRemaining);

      // If any package allows all platforms, don't filter
      if (hasAllPlatformsPackage) {
        console.log('⚠️ Allowing all platforms because one package allows all');
        setAvailablePlatforms(null);
        return;
      }

      // If we have specific platforms, set them; otherwise allow all
      if (platformsSet.size > 0) {
        const platformsArray = Array.from(platformsSet);
        console.log('✅ Setting available platforms:', platformsArray);
        setAvailablePlatforms(platformsArray);
      } else {
        // No specific platforms found - this shouldn't happen, but allow all as fallback
        console.log('⚠️ No specific platforms found, allowing all as fallback');
        setAvailablePlatforms(null);
      }
    } catch (err) {
      console.error('❌ Error fetching available platforms:', err);
      // On error, allow all platforms (don't block user)
      setAvailablePlatforms(null);
      setClientPackages([]);
      setTotalPostsRemaining(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoadingData(true);
      
      // Fetch clients
      const clientsResponse = await clientsAPI.getAll();
      setClients(clientsResponse.data.clients);

      // Fetch designers and writers for assignment
      const [designersResponse, writersResponse] = await Promise.all([
        usersAPI.getByRole('designer'),
        usersAPI.getByRole('writer'),
      ]);
      
      setDesigners(designersResponse.data.users || []);
      setWriters(writersResponse.data.users || []);

      // Fetch media files info
      const mediaPromises = mediaIds.map(id => mediaAPI.getById(id));
      const mediaResponses = await Promise.all(mediaPromises);
      setMediaFiles(mediaResponses.map(res => res.data.media));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleSpecificationsChange = (newSpecifications) => {
    setFormData(prev => ({
      ...prev,
      specifications: newSpecifications,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.client_id) {
      setError(isRTL ? 'يرجى اختيار العميل' : 'Please select a client');
      return;
    }

    if (!formData.platform) {
      setError(isRTL ? 'يرجى اختيار المنصة' : 'Please select a platform');
      return;
    }

    // Validate that client has enough posts remaining (carousel = 1 post, others = 1 per file)
    const postsNeeded = formData.content_type === 'carousel' ? 1 : mediaIds.length;
    if (formData.client_id && totalPostsRemaining !== null) {
      if (totalPostsRemaining === 0) {
        setError(isRTL 
          ? 'لا توجد بوستات متبقية في باقات العميل. يرجى إضافة باقة جديدة.' 
          : 'No posts remaining in client packages. Please add a new package.'
        );
        return;
      }
      if (totalPostsRemaining < postsNeeded) {
        setError(isRTL 
          ? (formData.content_type === 'carousel'
            ? `تحتاج بوست واحد. البوستات المتبقية: ${totalPostsRemaining}.`
            : `عدد البوستات المتبقية (${totalPostsRemaining}) أقل من عدد الملفات (${mediaIds.length}).`)
          : (formData.content_type === 'carousel'
            ? `Need 1 post. Posts remaining: ${totalPostsRemaining}.`
            : `Posts remaining (${totalPostsRemaining}) is less than files (${mediaIds.length}).`)
        );
        return;
      }
    }

    if (!formData.content_type) {
      setError(isRTL ? 'يرجى اختيار نوع المحتوى' : 'Please select a content type');
      return;
    }

    // Validate specifications (aspect_ratio is required)
    if (!formData.specifications?.aspect_ratio) {
      setError(isRTL ? 'يرجى تحديد نسبة العرض إلى الارتفاع' : 'Please specify aspect ratio');
      return;
    }

    setLoading(true);

    try {
      // Create posts using bulk API
      await mediaAPI.bulkCreatePosts({
        media_ids: mediaIds,
        client_id: formData.client_id,
        platform: formData.platform,
        content_type: formData.content_type,
        specifications: formData.specifications,
        assigned_designer_id: formData.assigned_designer_id || null,
        assigned_writer_id: formData.assigned_writer_id || null,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {isRTL ? 'إنشاء بوستات من الميديا' : 'Create Posts from Media'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {formData.content_type === 'carousel'
                  ? (isRTL 
                    ? `بوست كاروسال واحد سيحتوي على ${mediaIds.length} صورة` 
                    : `One carousel post with ${mediaIds.length} image(s)`)
                  : (isRTL 
                    ? `سيتم إنشاء ${mediaIds.length} بوست من الملفات المختارة` 
                    : `${mediaIds.length} post(s) will be created from selected media`)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Selected Media Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'الملفات المختارة' : 'Selected Files'}
            </h3>
            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg">
              {mediaFiles.map((file) => (
                <div key={file.id} className="aspect-square bg-gray-200 rounded overflow-hidden">
                  {file.file_type === 'image' ? (
                    <img
                      src={getImageUrl(file.file_url)}
                      alt={file.file_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-2xl">🎬</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Selection */}
            <div>
              <label htmlFor="client_id" className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'العميل' : 'Client'} <span className="text-red-500">*</span>
              </label>
              <select
                id="client_id"
                value={formData.client_id}
                onChange={(e) => handleChange('client_id', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">{isRTL ? 'اختر العميل' : 'Select a client'}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'المنصة' : 'Platform'} <span className="text-red-500">*</span>
              </label>
              <PlatformSelector
                value={formData.platform}
                onChange={(platform) => handleChange('platform', platform)}
                allowedPlatforms={availablePlatforms}
              />
            </div>

            {/* Content Type Selection */}
            {formData.platform && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'نوع المحتوى' : 'Content Type'} <span className="text-red-500">*</span>
                </label>
                <ContentTypeSelector
                  platform={formData.platform}
                  value={formData.content_type}
                  onChange={(contentType) => handleChange('content_type', contentType)}
                />
              </div>
            )}

            {/* Specifications Form */}
            {formData.platform && formData.content_type && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'المواصفات' : 'Specifications'}
                </label>
                <SpecificationsForm
                  platform={formData.platform}
                  contentType={formData.content_type}
                  specifications={formData.specifications}
                  onChange={handleSpecificationsChange}
                />
              </div>
            )}

            {/* Assignments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Designer Assignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'تعيين مصمم' : 'Assign Designer'}
                </label>
                <select
                  value={formData.assigned_designer_id}
                  onChange={(e) => handleChange('assigned_designer_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{isRTL ? 'غير معين' : 'Not assigned'}</option>
                  {designers.map((designer) => (
                    <option key={designer.id} value={designer.id}>
                      {designer.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Writer Assignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'تعيين كاتب' : 'Assign Writer'}
                </label>
                <select
                  value={formData.assigned_writer_id}
                  onChange={(e) => handleChange('assigned_writer_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{isRTL ? 'غير معين' : 'Not assigned'}</option>
                  {writers.map((writer) => (
                    <option key={writer.id} value={writer.id}>
                      {writer.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {formData.content_type === 'carousel' ? (
                  isRTL 
                    ? `سيتم إنشاء بوست واحد من نوع كاروسيل يحتوي على ${mediaIds.length} عنصر. يمكنك تعديله لاحقاً من صفحة البوستات.` 
                    : `1 carousel post will be created with ${mediaIds.length} items. You can edit it later from the posts page.`
                ) : (
                  isRTL 
                    ? `سيتم إنشاء ${mediaIds.length} بوست منفصل لكل ملف. يمكنك تعديل كل بوست لاحقاً من صفحة البوستات.` 
                    : `${mediaIds.length} separate post(s) will be created, one for each file. You can edit each post later from the posts page.`
                )}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading || !formData.client_id || !formData.platform || !formData.content_type || (totalPostsRemaining !== null && totalPostsRemaining === 0)}
              >
                {formData.content_type === 'carousel' 
                  ? (isRTL ? 'إنشاء بوست كاروسيل' : 'Create Carousel Post')
                  : (isRTL ? `إنشاء ${mediaIds.length} بوست` : `Create ${mediaIds.length} Post(s)`)
                }
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkPostCreatorModal;

